import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, fetchWithRetry, isLoading } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadEvent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({
        method: "get",
        url: `/api/users/events/${eventId}`,
      });
      setEvent(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi tải thông tin sự kiện:", error);
      if (error.response?.status === 404) {
        setError("Không tìm thấy sự kiện với ID này!");
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setError("Phiên đăng nhập hết hạn hoặc bạn không có quyền xem sự kiện này!");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setError(error.response?.data?.message || "Không thể tải thông tin sự kiện!");
      }
      setLoading(false);
    }
  }, [fetchWithRetry, eventId, navigate]);

  const handleJoinEvent = useCallback(async () => {
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/events/join",
        data: { eventId },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadEvent();
    } catch (error) {
      console.error("Lỗi tham gia sự kiện:", error);
      setError(error.response?.data?.message || "Không thể tham gia sự kiện!");
      setTimeout(() => setError(null), 3000);
    }
  }, [fetchWithRetry, eventId, loadEvent]);

  const handleLeaveEvent = useCallback(async () => {
    // Thêm thông báo xác nhận
    if (!window.confirm("Bạn có chắc chắn muốn bỏ tham gia sự kiện này không?")) {
      return;
    }

    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/events/leave",
        data: { eventId },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadEvent();
    } catch (error) {
      console.error("Lỗi bỏ tham gia sự kiện:", error);
      setError(error.response?.data?.message || "Không thể bỏ tham gia sự kiện!");
      setTimeout(() => setError(null), 3000);
    }
  }, [fetchWithRetry, eventId, loadEvent]);

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      setError("Vui lòng đăng nhập để xem chi tiết sự kiện!");
      setTimeout(() => navigate("/login"), 3000);
      return;
    }
    loadEvent();
  }, [user, navigate, loadEvent, isLoading]);

  if (loading) {
    return <p className="text-center text-gray-500">Đang tải...</p>;
  }

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-lg">
        {event ? (
          <>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{event.name}</h2>

            {error && <p className="text-red-500 mb-4">{error}</p>}
            {successMessage && (
              <p className={`mb-4 ${successMessage.includes("thành công") ? "text-green-500" : "text-red-500"}`}>
                {successMessage}
              </p>
            )}

            <div className="space-y-4">
              <p>
                <span className="font-medium">Ngày diễn ra:</span>{" "}
                {new Date(event.date).toLocaleDateString("vi-VN")}
              </p>
              <p>
                <span className="font-medium">Địa điểm:</span> {event.location}
              </p>
              <p>
                <span className="font-medium">Mô tả:</span> {event.description || "Không có mô tả"}
              </p>
              <p>
                <span className="font-medium">Trạng thái:</span> {event.status}
              </p>
              {event.unavailableReason && (
                <p className="text-red-500">
                  <span className="font-medium">Không khả dụng:</span> {event.unavailableReason}
                </p>
              )}
              <div>
                <span className="font-medium">Người tham gia:</span>
                {event.participants?.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {event.participants.map((participant) => (
                      <li key={participant._id} className="text-gray-600">
                        {participant.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 mt-2">Chưa có người tham gia.</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex space-x-4">
              {event.unavailableReason ? (
                <p className="text-gray-500 italic">Bạn không thể tham gia sự kiện này.</p>
              ) : event.participants?.some((p) => p._id === user._id) ? (
                <button
                  onClick={handleLeaveEvent}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
                >
                  Bỏ tham gia
                </button>
              ) : (
                <button
                  onClick={handleJoinEvent}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200"
                >
                  Tham gia sự kiện
                </button>
              )}
              <button
                onClick={() => navigate("/dashboard")}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Quay lại
              </button>
            </div>
          </>
        ) : (
          <p className="text-center text-gray-500">{error || "Không tìm thấy sự kiện."}</p>
        )}
      </div>
    </div>
  );
};

export default EventDetail;